<template>
  <o-field-group
    :label="label"
    :info="info"
    :col="option.type === 'bool'"
    :reverse="option.type === 'bool'"
  >
    <component
      :is="component"
      v-model="_currentValue"
      :options="options"
    />
  </o-field-group>
</template>


<script setup lang="ts">
import {
  OFieldGroup,
} from '@/components/elements'

import * as Fields from '@/components/fields'

import { CustomFieldDataType } from '~/_api/models/customField'

const { $i18n } = useNuxtApp()

interface Props {
  modelValue?: string | number | boolean | null
  option: Record<string, any>
  optionKey: string
  integration: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'update:option', value: any): void
}>()

const _currentValue = computed({
  get () {
    if (props.option.type === 'arrayInt') {
      return (props.modelValue as string)?.split(',') || []
    }

    return props.modelValue
  },
  set (value: any) {
    if (props.option.type === 'arrayInt') {
      emit('update:modelValue', value.join(','))
    } else {
      emit('update:modelValue', value)
    }
  },
})

const _option = computed({
  get () {
    return props.option
  },
  set (value: any) {
    emit('update:option', value)
  },
})

const component = computed(() => {
  switch (_option.value.type) {
    case CustomFieldDataType.date:
      return Fields.DateField
    case 'bool':
      return Fields.ToggleField
    case CustomFieldDataType.number:
      return Fields.NumberField
    case CustomFieldDataType.longtext:
      return Fields.TextAreaField
    case 'select':
      return Fields.SelectField
    case 'arrayInt':
      return Fields.ListField
    default:
      return Fields.TextField
  }
})

const options = computed(() => {
  const values = props.option?.values

  if (values) {
    const opts = Object.entries(values)

    return opts.map(([value, label]) => {
      return { value, label }
    })
  }

  return null
})

const label = computed(() => {
  const { integration, option, optionKey } = props

  const labels: Record<string, Record<string, string>> = {
    stripe: {
      stripeCurrency: $i18n.t('components.integration.edit.option.stripeCurrency'),
      country: $i18n.t('general.country'),
      request_customer_address: $i18n.t('components.integration.edit.option.requestCustomerAddress'),
    },
  }

  return labels[integration?.type]?.[optionKey] || option.label || optionKey
})

const info = computed(() => {
  const { integration, optionKey } = props

  const infoMap: Record<string, Record<string, string>> = {
    square: {
      payment_option_card: $i18n.t('components.integration.edit.option.paymentOptionCard'),
      payment_option_apple_pay: $i18n.t('components.integration.edit.option.paymentOptionApplePay'),
      payment_option_google_pay: $i18n.t('components.integration.edit.option.paymentOptionGooglePay'),
      payment_option_afterpay: $i18n.t('components.integration.edit.option.paymentOptionAfterpay'),
      allow_tipping: $i18n.t('components.integration.edit.option.allowTipping'),
      skip_receipt_screen: $i18n.t('components.integration.edit.option.skipReceiptScreen'),
      tip_percentages: $i18n.t('components.integration.edit.option.tipPercentages'),
      smart_tipping: $i18n.t('components.integration.edit.option.smartTipping'),
      custom_tip_field: $i18n.t('components.integration.edit.option.customTipField'),
      separate_tip_screen: $i18n.t('components.integration.edit.option.seperateTipScreen'),
    },
  }

  return infoMap[integration?.type]?.[optionKey]
})
</script>
